import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiError, type AdminCourseRow } from '../../api';

export function Courses() {
  const [params, setParams] = useSearchParams();
  const search = params.get('search') ?? '';
  const status = params.get('status') ?? '';

  const [rows, setRows] = useState<AdminCourseRow[]>([]);
  const [total, setTotal] = useState(0);
  const [picked, setPicked] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const q = new URLSearchParams({ pageSize: '200', sort: 'name' });
    if (search) q.set('search', search);
    if (status) q.set('status', status);
    try {
      const res = await api.get<{ total: number; courses: AdminCourseRow[] }>(`/admin/courses?${q}`);
      setRows(res.courses); setTotal(res.total);
    } catch (e) { setError((e as ApiError).message); }
    finally { setLoading(false); }
  }, [search, status]);

  useEffect(() => { void load(); }, [load]);

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const setStatus = async (newStatus: 'active' | 'inactive') => {
    const verb = newStatus === 'active' ? 'Switch on' : 'Switch off';
    const tail = newStatus === 'active'
      ? 'Trainees enrolled on them will see them immediately.'
      : 'Trainees will no longer see them. Nothing is deleted.';
    if (!confirm(`${verb} ${picked.length} course${picked.length === 1 ? '' : 's'}? ${tail}`)) return;
    setBusy(true); setError(null); setNote(null);
    try {
      const r = await api.post<{ changed: number }>('/admin/courses/status', { courseIds: picked, status: newStatus });
      setNote(`${r.changed} course${r.changed === 1 ? '' : 's'} switched ${newStatus === 'active' ? 'on' : 'off'}.`);
      setPicked([]); await load();
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  const offCount = rows.filter((c) => c.status !== 'active').length;

  return (
    <>
      <div className="page-head">
        <h1 className="h1">Courses</h1>
        <Link className="btn btn--primary" to="/admin/courses/new">New course</Link>
      </div>

      {offCount > 0 && !status && (
        <p className="muted small">
          {offCount} of {rows.length} courses are switched off — trainees cannot open those. Tick any you want
          live and switch them on; switching one off later hides it without touching anybody's record.
        </p>
      )}

      <div className="filters">
        <input placeholder="Search name or code" defaultValue={search}
          onKeyDown={(e) => { if (e.key === 'Enter') set('search', (e.target as HTMLInputElement).value.trim()); }}
          onBlur={(e) => set('search', e.target.value.trim())} aria-label="Search courses" />
        <select value={status} onChange={(e) => set('status', e.target.value)} aria-label="Status">
          <option value="">On and off</option>
          <option value="active">Live only</option>
          <option value="inactive">Switched off only</option>
        </select>
        <span className="muted">{total} course{total === 1 ? '' : 's'}</span>
      </div>

      {note && <p className="notice">{note}</p>}
      {error && <p className="auth__error">{error}</p>}

      {picked.length > 0 && (
        <div className="bulk">
          <span>{picked.length} selected</span>
          <button className="btn btn--primary" disabled={busy} onClick={() => void setStatus('active')}>Switch on</button>
          <button className="btn btn--ghost" disabled={busy} onClick={() => void setStatus('inactive')}>Switch off</button>
          <button className="btn btn--ghost" onClick={() => setPicked([])}>Clear</button>
        </div>
      )}

      <table className="grid">
        <thead>
          <tr>
            <th className="tick">
              <input type="checkbox" aria-label="Select all"
                checked={picked.length > 0 && picked.length === rows.length}
                onChange={(e) => setPicked(e.target.checked ? rows.map((r) => r.id) : [])} />
            </th>
            <th>Course</th><th>Lessons</th><th>Enrolled</th><th>Completed</th><th>Live</th>
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={6} className="muted">Loading…</td></tr>}
          {!loading && rows.map((c) => (
            <tr key={c.id} className={c.status !== 'active' ? 'is-off' : ''}>
              <td className="tick">
                <input type="checkbox" checked={picked.includes(c.id)} aria-label={`Select ${c.name}`}
                  onChange={(e) => setPicked(e.target.checked ? [...picked, c.id] : picked.filter((x) => x !== c.id))} />
              </td>
              <td>
                <Link to={`/admin/courses/${c.id}/edit`}>{c.name}</Link>
                {c.code && <span className="muted small mono"> {c.code}</span>}
                <div className="muted small"><Link to={`/admin/courses/${c.id}`}>who is on it</Link></div>
              </td>
              <td className="num">{c.unitCount}</td>
              <td className="num">{c.enrolledCount}</td>
              <td className="num">{c.completedCount}</td>
              <td>{c.status === 'active' ? <span className="pill pill--completed">on</span> : <span className="pill pill--incomplete">off</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
